import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Nav, Footer } from "@/components/Chrome";
import { NeuralField } from "@/components/NeuralField";
import { billingStatus } from "@/lib/api";

export function PaymentSuccess() {
  const [sub, setSub] = useState(null);
  const [tries, setTries] = useState(0);

  // Webhook is source of truth; poll status a few times while Stripe delivers the event.
  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const s = await billingStatus();
        if (!active) return;
        setSub(s);
        if (!s.has_access && tries < 5) setTimeout(() => setTries((t) => t + 1), 2000);
      } catch {
        if (tries < 5) setTimeout(() => setTries((t) => t + 1), 2000);
      }
    };
    check();
    return () => { active = false; };
  }, [tries]);

  return (
    <div className="relative min-h-screen text-[var(--text)]">
      <NeuralField />
      <Nav />
      <main className="relative max-w-lg mx-auto px-5 pt-24 pb-24 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="glass neon-border rounded-2xl p-10 scanlines" data-testid="payment-success">
          <CheckCircle2 size={48} className="text-neon-cyan mx-auto mb-5" />
          <h1 className="font-display text-3xl font-bold text-white">Your free trial is live.</h1>
          <p className="font-mono text-sm text-[var(--muted)] mt-4 leading-relaxed">
            Welcome to CoreText Pro. You won't be charged until your 30-day trial ends — cancel anytime before then.
          </p>
          <div className="mt-6 font-mono text-xs text-[var(--muted)] flex items-center justify-center gap-2" data-testid="success-status">
            {sub?.has_access ? (
              <span className="text-neon-cyan">Subscription confirmed ({sub.status}).</span>
            ) : (
              <><Loader2 size={14} className="animate-spin" /> Confirming your subscription…</>
            )}
          </div>
          <div className="mt-8 flex gap-3 justify-center">
            <Link to="/" data-testid="success-home" className="font-mono text-sm text-neon-cyan hover:underline">Start auditing →</Link>
            <Link to="/pro" className="font-mono text-sm text-[var(--muted)] hover:text-neon-cyan">Manage plan</Link>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="relative min-h-screen text-[var(--text)]">
      <NeuralField />
      <Nav />
      <main className="relative max-w-lg mx-auto px-5 pt-24 pb-24 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="glass neon-border rounded-2xl p-10 scanlines" data-testid="payment-cancel">
          <XCircle size={48} className="text-neon-amber mx-auto mb-5" />
          <h1 className="font-display text-3xl font-bold text-white">Checkout canceled.</h1>
          <p className="font-mono text-sm text-[var(--muted)] mt-4 leading-relaxed">
            No worries — you weren't charged. You can start your 30-day free trial whenever you're ready.
          </p>
          <div className="mt-8 flex gap-3 justify-center">
            <Link to="/pro" data-testid="cancel-retry" className="font-mono text-sm text-neon-cyan hover:underline">Back to CoreText Pro →</Link>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
